import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Flame, AlertTriangle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling");
  const [rewards, setRewards] = useState(null);

  useEffect(() => {
    sessionStorage.removeItem("noxway_checkout_started");
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setRewards(data.rewards || null);
          setState("paid");
          return;
        }
      } catch (e) {
        // keep polling
      }
      attempts += 1;
      if (attempts < 15) setTimeout(poll, 2000);
      else setState("timeout");
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#08090d] text-white flex items-center justify-center px-6" data-testid="payment-success-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="card-glow-border rounded-3xl p-10 lg:p-14 max-w-lg w-full text-center"
      >
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-[#f97316] to-[#ea580c] shadow-[0_0_48px_rgba(255,98,31,0.5)] mb-8">
          <Flame size={28} className="text-white" strokeWidth={2.2} />
        </div>

        {state === "polling" && (
          <>
            <Loader2 size={28} className="animate-spin text-[#f97316] mx-auto mb-6" />
            <h1 className="font-display font-bold text-2xl sm:text-3xl mb-3" data-testid="payment-confirming-title">Confirming your order…</h1>
            <p className="text-slate-400 text-sm sm:text-base">Hold tight while we verify your payment with Stripe.</p>
          </>
        )}

        {state === "paid" && (
          <>
            <CheckCircle2 size={40} className="text-[#f97316] mx-auto mb-6" />
            <h1 className="font-display font-bold text-2xl sm:text-3xl mb-3" data-testid="payment-success-title">Payment received.</h1>
            <p className="text-slate-400 text-sm sm:text-base mb-2" data-testid="payment-success-message">
              Your NOXWAY™ Formula 100 order is confirmed. A receipt is on its way to your inbox, and our team
              will reach out with shipping and application guidance.
            </p>
            {rewards && (
              <div className="my-6 rounded-2xl border border-[#f97316]/30 bg-[#f97316]/5 px-6 py-5" data-testid="rewards-block">
                <div className="font-tech text-[10px] tracking-[0.25em] text-[#f97316] uppercase mb-2">NOXWAY Rewards</div>
                <p className="text-sm text-slate-200">
                  You earned <strong className="text-[#f97316]">{rewards.points_earned} points</strong> on this order
                  {rewards.total_points != null && <> — balance: <strong>{rewards.total_points}</strong></>}.
                </p>
                {rewards.referral_code && (
                  <p className="text-xs text-slate-400 mt-2">
                    Share your code <span className="font-tech text-[#f97316] font-bold">{rewards.referral_code}</span> —
                    earn {rewards.referral_bonus} bonus points when a friend completes their first order.
                  </p>
                )}
              </div>
            )}
            <p className="font-tech text-[10px] tracking-[0.2em] text-slate-600 uppercase mb-10">Order ref: {sessionId?.slice(-12)}</p>
          </>
        )}

        {(state === "timeout" || state === "error") && (
          <>
            <AlertTriangle size={40} className="text-amber-400 mx-auto mb-6" />
            <h1 className="font-display font-bold text-2xl sm:text-3xl mb-3" data-testid="payment-pending-title">Still processing.</h1>
            <p className="text-slate-400 text-sm sm:text-base mb-10">
              Your payment is being processed. If you were charged, your order is safe — contact
              sales@noxway.shop with any questions.
            </p>
          </>
        )}

        <Link
          to="/"
          data-testid="back-to-site-link"
          className="inline-flex items-center gap-2.5 font-display font-bold tracking-[0.12em] text-sm px-8 py-4 rounded-full border border-white/25 text-white hover:border-[#f97316] hover:text-[#f97316] transition-colors duration-300"
        >
          BACK TO NOXWAY
        </Link>
      </motion.div>
    </div>
  );
}
